import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiArrowLeft,
  FiSettings,
  FiMessageCircle,
  FiCheckSquare,
  FiFolder,
  FiBookOpen,
  FiCalendar,
  FiUsers,
  FiX,
} from "react-icons/fi";
import { useRoom } from "../../context/RoomContext";
import { useUserContext } from "../../context/UserContext";
import ChatTab from "./tabs/ChatTab";
import TasksTab from "./tabs/TasksTab";
import FilesTab from "./tabs/FilesTab";
import LibraryTab from "./tabs/LibraryTab";
import CalendarTab from "./tabs/CalendarTab";
import MembersSidebar from "./components/MembersSidebar";
import RoomSettingsModal from "./components/RoomSettingsModal";
import LoadingBar from "../ui/LoadingBar";
import "./RoomDetail.css";

// ==============================================
// Tab Configuration
// ==============================================
const TABS = [
  {
    id: "chat",
    label: "Chat",
    icon: FiMessageCircle,
    component: ChatTab,
    color: "#3b82f6",
    bg: "#dbeafe",
  },
  {
    id: "tasks",
    label: "Tasks",
    icon: FiCheckSquare,
    component: TasksTab,
    color: "#10b981",
    bg: "#d1fae5",
  },
  {
    id: "files",
    label: "Files",
    icon: FiFolder,
    component: FilesTab,
    color: "#f59e0b",
    bg: "#fef3c7",
  },
  {
    id: "library",
    label: "Library",
    icon: FiBookOpen,
    component: LibraryTab,
    color: "#8b5cf6",
    bg: "#ede9fe",
  },
  {
    id: "calendar",
    label: "Calendar",
    icon: FiCalendar,
    component: CalendarTab,
    color: "#ec4899",
    bg: "#fce7f3",
  },
];

// ==============================================
// Animation Variants
// ==============================================
const headerVariants = {
  hidden: { opacity: 0, y: -20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
};

const contentVariants = {
  hidden: { opacity: 0, scale: 0.98 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.2 } },
  exit: { opacity: 0, scale: 0.98, transition: { duration: 0.15 } },
};

const sidebarVariants = {
  hidden: { x: "100%", opacity: 0 },
  visible: {
    x: 0,
    opacity: 1,
    transition: { type: "spring", damping: 25, stiffness: 300 },
  },
  exit: { x: "100%", opacity: 0, transition: { duration: 0.2 } },
};

const overlayVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
};

// ==============================================
// Sub-Components
// ==============================================

// Back Button
const BackButton = ({ onClick }) => (
  <motion.button
    className="rdet-back-btn"
    onClick={onClick}
    whileHover={{ scale: 1.05, x: -2 }}
    whileTap={{ scale: 0.95 }}
  >
    <FiArrowLeft />
  </motion.button>
);

// Room Title
const RoomTitle = ({ name }) => (
  <div className="rdet-title">
    <h1>{name}</h1>
  </div>
);

// Tab Button
const TabButton = ({ tab, isActive, onClick }) => {
  const Icon = tab.icon;

  return (
    <motion.button
      className={`rdet-tab-btn ${isActive ? "rdet-tab-btn--active" : ""}`}
      style={{ "--tab-color": tab.color, "--tab-bg": tab.bg }}
      onClick={onClick}
      title={tab.label}
      whileHover={{ scale: 1.05, y: -2 }}
      whileTap={{ scale: 0.95 }}
    >
      <div className="rdet-tab-icon">
        <Icon />
      </div>
      <span className="rdet-tab-label">{tab.label}</span>
    </motion.button>
  );
};

// Tab Navigation
const TabNavigation = ({ tabs, activeTab, onTabChange }) => (
  <div className="rdet-tabs">
    {tabs.map((tab) => (
      <TabButton
        key={tab.id}
        tab={tab}
        isActive={activeTab === tab.id}
        onClick={() => onTabChange(tab.id)}
      />
    ))}
  </div>
);

// Members Toggle Button
const MembersToggle = ({ count, isActive, onClick }) => (
  <motion.button
    className={`rdet-members-btn ${isActive ? "rdet-members-btn--active" : ""}`}
    onClick={onClick}
    whileHover={{ scale: 1.02 }}
    whileTap={{ scale: 0.98 }}
    title={isActive ? "Hide Members" : "Show Members"}
  >
    <FiUsers />
    <span className="rdet-members-count">{count}</span>
  </motion.button>
);

// Settings Button
const SettingsButton = ({ onClick }) => (
  <motion.button
    className="rdet-settings-btn"
    onClick={onClick}
    whileHover={{ scale: 1.05, rotate: 45 }}
    whileTap={{ scale: 0.95 }}
    title="Lab Settings"
  >
    <FiSettings />
  </motion.button>
);

// Header Component
const RoomHeader = ({
  room,
  activeTab,
  onBack,
  onTabChange,
  showMembers,
  onToggleMembers,
  onOpenSettings,
  isMember,
}) => (
  <motion.header
    className="rdet-header"
    variants={headerVariants}
    initial="hidden"
    animate="visible"
  >
    <div className="rdet-header-left">
      <BackButton onClick={onBack} />
      <RoomTitle name={room.name} />
    </div>

    <div className="rdet-header-center">
      <TabNavigation
        tabs={TABS}
        activeTab={activeTab}
        onTabChange={onTabChange}
      />
    </div>

    <div className="rdet-header-right">
      <div className="rdet-header-divider" />
      <MembersToggle
        count={room.members?.length || 0}
        isActive={showMembers}
        onClick={onToggleMembers}
      />
      {isMember && <SettingsButton onClick={onOpenSettings} />}
    </div>
  </motion.header>
);

// Mobile Members Panel
const MobileMembers = ({ room, userRole, isOpen, onClose }) => (
  <AnimatePresence>
    {isOpen && (
      <>
        <motion.div
          className="rdet-overlay"
          variants={overlayVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          onClick={onClose}
        />
        <motion.div
          className="rdet-mobile-members"
          variants={sidebarVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          <div className="rdet-mobile-members-header">
            <h3>
              <FiUsers />
              <span>Members</span>
              <span className="rdet-mobile-members-count">
                {room.members?.length || 0}
              </span>
            </h3>
            <motion.button
              className="rdet-mobile-close"
              onClick={onClose}
              whileHover={{ scale: 1.1, rotate: 90 }}
              whileTap={{ scale: 0.9 }}
            >
              <FiX />
            </motion.button>
          </div>
          <div className="rdet-mobile-members-content">
            <MembersSidebar room={room} userRole={userRole} />
          </div>
        </motion.div>
      </>
    )}
  </AnimatePresence>
);

// Loading State
const LoadingState = () => (
  <div className="rdet-loading">
    <LoadingBar text="Loading lab..." />
  </div>
);

// Error State
const ErrorState = ({ message, onBack }) => (
  <motion.div
    className="rdet-error"
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
  >
    <div className="rdet-error-icon">⚠️</div>
    <h2>Something went wrong</h2>
    <p>{message}</p>
    <motion.button
      className="rdet-error-btn"
      onClick={onBack}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      <FiArrowLeft />
      <span>Back to Labs</span>
    </motion.button>
  </motion.div>
);

// ==============================================
// Main Component
// ==============================================
const RoomDetail = () => {
  const { roomId, tab } = useParams();
  const navigate = useNavigate();
  const { user } = useUserContext();
  const roomDetailRef = useRef(null);

  const { currentRoom, fetchRoomById, getUserRoleInRoom, loading, error } =
    useRoom();

  const [activeTab, setActiveTab] = useState(tab || "chat");
  const [showMembersSidebar, setShowMembersSidebar] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const userId = user?.id || user?._id;

  // Reset scroll positions when entering room
  useLayoutEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }

    const resetScroll = () => {
      window.scrollTo(0, 0);
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;

      const sidebarLayout = document.querySelector(
        ".research-rooms-sidebar-layout"
      );
      if (sidebarLayout) {
        sidebarLayout.scrollTop = 0;
      }
    };

    resetScroll();
    const timeout1 = setTimeout(resetScroll, 50);
    const timeout2 = setTimeout(resetScroll, 150);

    return () => {
      clearTimeout(timeout1);
      clearTimeout(timeout2);
      if ("scrollRestoration" in window.history) {
        window.history.scrollRestoration = "auto";
      }
    };
  }, [roomId]);

  // Load room data
  useEffect(() => {
    if (roomId) {
      fetchRoomById(roomId);
    }
  }, [roomId, fetchRoomById]);

  // Sync tab from URL
  useEffect(() => {
    if (tab && TABS.find((t) => t.id === tab)) {
      setActiveTab(tab);
    }
  }, [tab]);

  // Handle tab change
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    navigate(`/labs/${roomId}/${tabId}`, { replace: true });
  };

  // Handle back navigation
  const handleBack = () => {
    navigate("/labs");
  };

  // Toggle members sidebar
  const handleToggleMembers = () => {
    setShowMembersSidebar((prev) => !prev);
  };

  // Get user role
  const userRole = currentRoom ? getUserRoleInRoom(currentRoom, userId) : null;

  // Redirect non-members to preview page
  useEffect(() => {
    if (currentRoom && userRole && !userRole.isMember && !userRole.isLeader) {
      navigate(`/labs/${roomId}/preview`, { replace: true });
    }
  }, [currentRoom, userRole, roomId, navigate]);

  // Loading state
  if (loading && !currentRoom) {
    return <LoadingState />;
  }

  // Error state
  if (error && !currentRoom) {
    return <ErrorState message={error} onBack={handleBack} />;
  }

  // No room found
  if (!currentRoom) {
    return null;
  }

  // Get active tab component
  const ActiveTabComponent =
    TABS.find((t) => t.id === activeTab)?.component || ChatTab;

  return (
    <div className="rdet-container" ref={roomDetailRef}>
      {/* Header */}
      <RoomHeader
        room={currentRoom}
        activeTab={activeTab}
        onBack={handleBack}
        onTabChange={handleTabChange}
        showMembers={showMembersSidebar}
        onToggleMembers={handleToggleMembers}
        onOpenSettings={() => setShowSettings(true)}
        isMember={userRole?.isMember}
      />

      {/* Main Content */}
      <div className="rdet-content">
        {/* Tab Content */}
        <div className="rdet-tab-content">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              className="rdet-tab-wrapper"
              variants={contentVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <ActiveTabComponent roomId={roomId} userRole={userRole} roomMembers={currentRoom.members || []} />
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Desktop Sidebar */}
        <AnimatePresence>
          {showMembersSidebar && (
            <motion.aside
              className="rdet-sidebar"
              variants={sidebarVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <MembersSidebar room={currentRoom} userRole={userRole} />
            </motion.aside>
          )}
        </AnimatePresence>
      </div>

      {/* Mobile Members Panel */}
      <MobileMembers
        room={currentRoom}
        userRole={userRole}
        isOpen={showMembersSidebar}
        onClose={() => setShowMembersSidebar(false)}
      />

      {/* Room Settings Modal */}
      <RoomSettingsModal
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        room={currentRoom}
        userRole={userRole}
      />
    </div>
  );
};

export default RoomDetail;
